import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading, Logo } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney, parseMoney } from '../finance/money';
import { QrCode } from '../nursery/QrCode';
import { useMe } from '../session/SessionProvider';

type Status = 'nuevo' | 'preparando' | 'listo' | 'entregado' | 'cancelado';
type Order = {
  id: string; number: number; customerName: string; phone: string | null; seller: { personId: string; name: string } | null; delivery: boolean; address: string | null; addressRefs: string | null;
  zone: string | null; mapsUrl: string | null; driver: string | null; notes: string | null; status: Status; paid: boolean; paidMethod: string | null; totalCents: number; createdAt: string;
  items: { productId: string; name: string; unitPriceCents: number; quantity: number }[];
};
type Detail = {
  id: string; title: string; description: string | null; ministryId: string | null; ministryName: string | null; goalCents: number | null; ordersCloseAt: string | null; deliveryDate: string | null;
  pickupPlace: string | null; deliveryEnabled: boolean; deliveryFeeCents: number; status: 'abierta' | 'cerrada' | 'terminada'; open: boolean; canManage: boolean; publicPath: string | null;
  products: { id: string; name: string; priceCents: number; unitCostCents: number | null; active: boolean }[];
  orders: Order[];
  totals: { orders: number; orderedCents: number; paidCents: number; pendingCents: number; costCents: number; expensesCents: number; profitCents: number } | null;
  production: { productId: string; name: string; quantity: number }[] | null;
  sellers: { name: string; orders: number; totalCents: number }[] | null;
  expenses: { id: string; concept: string; amountCents: number }[] | null;
};

const money = (c: number) => formatMoney(c, 'MXN');
const STATUS: Record<Status, string> = { nuevo: 'Nuevo', preparando: 'Preparando', listo: 'Listo', entregado: 'Entregado', cancelado: 'Cancelado' };
const NEXT: Partial<Record<Status, Status>> = { nuevo: 'preparando', preparando: 'listo', listo: 'entregado' };
const day = (iso: string | null) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString('es', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');

/** Recaudaciones de la iglesia: lista y alta. */
export function FundraisersPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/commerce/churches/${churchId}/fundraisers`;
  const data = useAsync((signal) => (churchId ? api.get<{ canCreate: boolean; items: { id: string; title: string; status: string; open: boolean; goalCents: number | null; raisedCents: number; deliveryDate: string | null }[] }>(base, signal) : Promise.resolve(null)), [base]);
  const ministries = useAsync((signal) => (churchId ? api.get<{ items: { id: string; name: string }[] }>(`/organization/churches/${churchId}/ministries`, signal).then((r) => r.items) : Promise.resolve([])), [churchId]);
  const navigate = useNavigate();
  // «Recaudación» desde la ficha del ministerio: llega con ?ministerio=… y abre el formulario con él elegido.
  const preset = useSearchParams()[0].get('ministerio');
  const [open, setOpen] = useState(Boolean(preset));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const close = t('closeAt');
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.post<{ id: string }>(base, {
        title: t('title'), description: t('description'), ministryId: t('ministryId'), goalCents: t('goal') ? parseMoney(t('goal')!) : null,
        ordersCloseAt: close ? new Date(close).toISOString() : null, deliveryDate: t('deliveryDate'), pickupPlace: t('pickupPlace'),
        deliveryEnabled: f.get('deliveryEnabled') === 'on', deliveryFeeCents: t('deliveryFee') ? parseMoney(t('deliveryFee')!) ?? 0 : 0,
      });
      navigate(`/app/recaudaciones/${id}?ver=productos`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page__header">
        <h1>Recaudaciones</h1>
        <p className="muted">Ventas para juntar fondos (tamales, chocolates, camisetas…) con pedidos anticipados y entrega.</p>
      </header>
      {data.status === 'ready' && !data.data?.canCreate ? (
        <p className="small muted">Las recaudaciones las organizan el pastor, Diaconado y los líderes de ministerio.</p>
      ) : data.status !== 'ready' ? null : !open ? (
        <div><Button onClick={() => setOpen(true)}>Nueva recaudación</Button></div>
      ) : (
        <Card title="Nueva recaudación">
          <form className="form" onSubmit={create}>
            {error ? <Alert>{error}</Alert> : null}
            <Field label="Nombre" name="title" required minLength={3} maxLength={120} placeholder="Tamales para el campamento" />
            <div className="form__row">
              <div className="field">
                <label htmlFor="fr-ministry">Ministerio que recauda</label>
                <select id="fr-ministry" name="ministryId" key={ministries.status} defaultValue={preset && ministries.status === 'ready' && ministries.data.some((m) => m.id === preset) ? preset : ''}>
                  <option value="">La iglesia</option>
                  {(ministries.status === 'ready' ? ministries.data : []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <Field label="Meta (opcional)" name="goal" inputMode="decimal" placeholder="5000" />
            </div>
            <div className="form__row">
              <Field label="Pedidos hasta (preventa)" name="closeAt" type="datetime-local" />
              <Field label="Día de entrega" name="deliveryDate" type="date" />
            </div>
            <Field label="Dónde se recoge (opcional)" name="pickupPlace" maxLength={200} placeholder="En el templo después del culto" />
            <label className="check"><input type="checkbox" name="deliveryEnabled" /> También entrega a domicilio</label>
            <Field label="Costo de envío (opcional)" name="deliveryFee" inputMode="decimal" placeholder="30" />
            <div className="field">
              <label htmlFor="fr-desc">Descripción (opcional)</label>
              <textarea id="fr-desc" name="description" rows={2} maxLength={2000} />
            </div>
            <div className="actions">
              <Button type="submit" busy={busy}>Crear y agregar productos</Button>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            </div>
          </form>
        </Card>
      )}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data ? (
        data.data.items.length === 0 ? (
          <Card><EmptyState title="Todavía no hay recaudaciones." /></Card>
        ) : (
          <ul className="list card">
            {data.data.items.map((f) => (
              <li key={f.id} className="list__item">
                <Link className="list__link" to={`/app/recaudaciones/${f.id}`}>
                  <strong>{f.title}</strong>
                  <span className="small muted">
                    {f.open ? 'Recibiendo pedidos' : f.status === 'terminada' ? 'Terminada' : 'Preventa cerrada'} · {money(f.raisedCents)} cobrado{f.goalCents ? ` de ${money(f.goalCents)}` : ''}
                    {f.deliveryDate ? ` · Entrega ${day(f.deliveryDate)}` : ''}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

/** Formulario de pedido (vendedor en la app o enlace público). */
export function OrderForm({ products, deliveryEnabled, deliveryFeeCents, busy, submitLabel, onSubmit }: {
  products: { id: string; name: string; priceCents: number }[]; deliveryEnabled: boolean; deliveryFeeCents: number; busy: boolean; submitLabel: string; onSubmit: (body: object) => Promise<boolean>;
}) {
  const [qty, setQty] = useState<Record<string, number>>({});
  const [delivery, setDelivery] = useState(false);
  const subtotal = products.reduce((t, p) => t + p.priceCents * (qty[p.id] ?? 0), 0);
  const total = subtotal + (delivery ? deliveryFeeCents : 0);
  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const f = new FormData(form);
        const t = (k: string) => String(f.get(k) ?? '').trim() || null;
        void onSubmit({
          customerName: t('customerName'), phone: t('phone'), items: products.filter((p) => (qty[p.id] ?? 0) > 0).map((p) => ({ productId: p.id, quantity: qty[p.id] })),
          delivery, address: t('address'), addressRefs: t('addressRefs'), zone: t('zone'), mapsUrl: null, notes: t('notes'),
        }).then((ok) => {
          if (ok) {
            form.reset();
            setQty({});
            setDelivery(false);
          }
        });
      }}
    >
      <ul className="list">
        {products.map((p) => (
          <li key={p.id} className="list__item">
            <span>{p.name} · <strong>{money(p.priceCents)}</strong></span>
            <input
              aria-label={`Cantidad de ${p.name}`}
              type="number"
              min={0}
              max={999}
              className="qty-input"
              value={qty[p.id] ?? 0}
              onChange={(e) => setQty({ ...qty, [p.id]: Math.max(0, Math.min(999, Number(e.target.value) || 0)) })}
            />
          </li>
        ))}
      </ul>
      <div className="form__row">
        <Field label="Nombre" name="customerName" required maxLength={120} />
        <Field label="Teléfono" name="phone" type="tel" maxLength={30} />
      </div>
      {deliveryEnabled ? (
        <label className="check">
          <input type="checkbox" checked={delivery} onChange={(e) => setDelivery(e.target.checked)} /> Entrega a domicilio{deliveryFeeCents ? ` (+${money(deliveryFeeCents)})` : ''}
        </label>
      ) : null}
      {delivery ? (
        <>
          <Field label="Domicilio" name="address" required maxLength={300} />
          <div className="form__row">
            <Field label="Referencias" name="addressRefs" maxLength={300} placeholder="Casa azul, junto a la tienda" />
            <Field label="Colonia o zona" name="zone" maxLength={80} />
          </div>
        </>
      ) : null}
      <Field label="Notas (opcional)" name="notes" maxLength={500} />
      <p className="pos__total">Total <strong className="money">{money(total)}</strong></p>
      <Button type="submit" busy={busy} disabled={subtotal === 0}>{submitLabel}</Button>
    </form>
  );
}

export function FundraiserPage() {
  const { fundraiserId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const url = `/commerce/churches/${churchId}/fundraisers/${fundraiserId}`;
  const data = useAsync((signal) => (churchId ? api.get<Detail>(url, signal) : Promise.resolve(null)), [url]);
  const [params, setParams] = useSearchParams();
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const d = data.data;
  if (!d) return null;
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const tabs: [string, string][] = d.canManage
    ? [['resumen', 'Resumen'], ['pedidos', `Pedidos (${d.orders.filter((o) => o.status !== 'cancelado').length})`], ['entregas', 'Entregas'], ['productos', 'Productos y gastos']]
    : [['pedidos', 'Mis ventas']];
  const tab = tabs.find(([k]) => k === params.get('ver'))?.[0] ?? tabs[0]![0];
  const link = d.publicPath ? `${window.location.origin}${d.publicPath}` : null;
  const active = d.products.filter((p) => p.active);

  return (
    <div className="page">
      <p className="small"><Link to="/app/recaudaciones">← Recaudaciones</Link></p>
      <header className="page__header">
        <h1>{d.title}</h1>
        <p className="muted">
          {d.ministryName ? `${d.ministryName} · ` : ''}{d.open ? `Recibiendo pedidos${d.ordersCloseAt ? ` hasta el ${day(d.ordersCloseAt)}` : ''}` : 'Preventa cerrada'}
          {d.deliveryDate ? ` · Entrega ${day(d.deliveryDate)}` : ''}{d.pickupPlace ? ` · ${d.pickupPlace}` : ''}
        </p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {tabs.length > 1 ? (
        <div className="tabs tabs--scroll" role="tablist" aria-label="Recaudación">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tabs__tab${tab === k ? ' tabs__tab--active' : ''}`} onClick={() => setParams({ ver: k }, { replace: true })}>{label}</button>
          ))}
        </div>
      ) : null}

      {tab === 'resumen' && d.totals ? (
        <>
          <div className="stats">
            <div><strong>{money(d.totals.paidCents)}</strong><span>cobrado{d.goalCents ? ` de ${money(d.goalCents)}` : ''}</span></div>
            <div><strong>{money(d.totals.pendingCents)}</strong><span>por cobrar</span></div>
            <div><strong>{money(d.totals.expensesCents)}</strong><span>gastos</span></div>
            <div><strong>{money(d.totals.profitCents)}</strong><span>ganancia (cobrado − gastos)</span></div>
            <div><strong>{d.totals.orders}</strong><span>pedidos</span></div>
          </div>
          {d.goalCents ? <progress className="usage" max={d.goalCents} value={Math.min(d.totals.paidCents, d.goalCents)} aria-label="Avance de la meta" /> : null}
          <Card title="Qué preparar">
            <ul className="list">
              {(d.production ?? []).map((p) => <li key={p.productId} className="list__item"><span>{p.name}</span><strong>{p.quantity}</strong></li>)}
            </ul>
          </Card>
          {d.sellers && d.sellers.length ? (
            <Card title="Vendedores">
              <ul className="list">
                {d.sellers.map((s) => <li key={s.name} className="list__item"><span>{s.name} · {s.orders} pedidos</span><strong>{money(s.totalCents)}</strong></li>)}
              </ul>
            </Card>
          ) : null}
          {link ? (
            <Card title="Enlace para pedir sin cuenta">
              <div className="qr-print">
                <QrCode value={link} label="Código QR para hacer un pedido" />
                <p className="center">{d.title}: escanea para hacer tu pedido.</p>
              </div>
              <div className="code-row"><code className="code">{link}</code></div>
              <div className="actions actions--wrap">
                <a className="btn btn--secondary" href={`https://wa.me/?text=${encodeURIComponent(`${d.title}: haz tu pedido aquí ${link}`)}`} target="_blank" rel="noreferrer">Enviar por WhatsApp</a>
                <Button variant="ghost" onClick={() => window.print()}>Imprimir QR</Button>
              </div>
            </Card>
          ) : null}
          <Card title="Estado">
            <div className="actions actions--wrap">
              {(['abierta', 'cerrada', 'terminada'] as const).filter((s) => s !== d.status).map((s) => (
                <Button key={s} variant="secondary" busy={busy} onClick={() => s === 'terminada' && !window.confirm('¿Terminar la recaudación? Ya no se podrán tomar pedidos.') ? undefined : void run(() => api.put(url, { title: d.title, description: d.description, ministryId: d.ministryId, goalCents: d.goalCents, ordersCloseAt: d.ordersCloseAt, deliveryDate: d.deliveryDate, pickupPlace: d.pickupPlace, deliveryEnabled: d.deliveryEnabled, deliveryFeeCents: d.deliveryFeeCents, status: s }), 'Estado actualizado.')}>
                  {s === 'abierta' ? 'Reabrir pedidos' : s === 'cerrada' ? 'Cerrar preventa' : 'Terminar recaudación'}
                </Button>
              ))}
            </div>
          </Card>
        </>
      ) : null}

      {tab === 'pedidos' ? (
        <>
          {d.open || d.canManage ? (
            <Card title="Tomar un pedido">
              {active.length === 0 ? <p className="muted small">Primero agrega productos.</p> : (
                <OrderForm products={active} deliveryEnabled={d.deliveryEnabled} deliveryFeeCents={d.deliveryFeeCents} busy={busy} submitLabel="Guardar pedido"
                  onSubmit={(b) => run(() => api.post(`${url}/orders`, b), 'Pedido guardado.')} />
              )}
            </Card>
          ) : null}
          <ul className="list card">
            {d.orders.map((o) => (
              <li key={o.id} className="list__item list__item--stack">
                <span>
                  <strong>#{o.number} · {o.customerName}</strong> · {money(o.totalCents)} · {STATUS[o.status]} · {o.paid ? `Pagado (${o.paidMethod})` : 'Sin pagar'}
                  <span className="small muted"> · {o.items.map((i) => `${i.quantity} ${i.name}`).join(', ')}{o.delivery ? ' · a domicilio' : ''}{o.seller ? ` · vendió ${o.seller.name}` : ''}</span>
                </span>
                {o.status !== 'cancelado' ? (
                  <div className="actions actions--wrap">
                    {!o.paid ? (
                      <select aria-label={`Registrar pago del pedido ${o.number}`} defaultValue="" disabled={busy} onChange={(e) => {
                        const method = e.target.value;
                        if (!method) return;
                        if (!window.confirm(`¿Registrar el pago del pedido ${o.number} (${money(o.totalCents)}) en ${method}?`)) {
                          e.target.value = '';
                          return;
                        }
                        void run(() => api.post(`${url}/orders/${o.id}/pay`, { method }), `Pedido ${o.number} pagado.`);
                      }}>
                        <option value="">Registrar pago…</option>
                        <option value="efectivo">Efectivo</option>
                        <option value="transferencia">Transferencia</option>
                        <option value="otro">Otro</option>
                      </select>
                    ) : null}
                    {d.canManage && NEXT[o.status] ? <Button variant="secondary" busy={busy} onClick={() => void run(() => api.put(`${url}/orders/${o.id}/status`, { status: NEXT[o.status] }), 'Listo.')}>{STATUS[NEXT[o.status]!]}</Button> : null}
                    {d.canManage ? <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm(`¿Cancelar el pedido ${o.number}?`)) void run(() => api.put(`${url}/orders/${o.id}/status`, { status: 'cancelado' }), 'Pedido cancelado.'); }}>Cancelar</Button> : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {tab === 'entregas' ? <Deliveries orders={d.orders.filter((o) => o.status !== 'cancelado')} url={url} busy={busy} run={run} /> : null}

      {tab === 'productos' ? (
        <>
          <Card title="Productos">
            <ul className="list">
              {d.products.map((p) => (
                <li key={p.id} className="list__item">
                  <span>{p.name} · {money(p.priceCents)}{p.unitCostCents !== null ? <span className="small muted"> · cuesta {money(p.unitCostCents)}</span> : null}{!p.active ? ' · (pausado)' : ''}</span>
                  <Button variant="ghost" busy={busy} onClick={() => void run(() => api.put(`${url}/products/${p.id}`, { name: p.name, priceCents: p.priceCents, unitCostCents: p.unitCostCents, active: !p.active }), 'Producto actualizado.')}>{p.active ? 'Pausar' : 'Activar'}</Button>
                </li>
              ))}
            </ul>
            <form
              className="family-add"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const f = new FormData(form);
                const price = parseMoney(String(f.get('price') ?? ''));
                const cost = String(f.get('cost') ?? '').trim() ? parseMoney(String(f.get('cost'))) : null;
                if (price === null) return setMsg({ tone: 'error', text: 'Escribe el precio.' });
                void run(() => api.post(`${url}/products`, { name: String(f.get('name') ?? '').trim(), priceCents: price, unitCostCents: cost }), 'Producto agregado.').then((ok) => ok && form.reset());
              }}
            >
              <Field label="Producto" name="name" required maxLength={80} placeholder="Tamal de rajas" />
              <Field label="Precio" name="price" inputMode="decimal" required />
              <Field label="Costo por pieza (opcional)" name="cost" inputMode="decimal" />
              <Button type="submit" variant="secondary" busy={busy}>Agregar</Button>
            </form>
          </Card>
          <Card title="Gastos">
            <p className="small muted">Se registran también en Finanzas (cuenta «Recaudaciones»).</p>
            <ul className="list">
              {(d.expenses ?? []).map((x) => <li key={x.id} className="list__item"><span>{x.concept}</span><strong>{money(x.amountCents)}</strong></li>)}
            </ul>
            <form
              className="family-add"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const f = new FormData(form);
                const amount = parseMoney(String(f.get('amount') ?? ''));
                if (amount === null) return setMsg({ tone: 'error', text: 'Escribe el monto del gasto.' });
                void run(() => api.post(`${url}/expenses`, { concept: String(f.get('concept') ?? '').trim(), amountCents: amount }), 'Gasto registrado.').then((ok) => ok && form.reset());
              }}
            >
              <Field label="Concepto" name="concept" required maxLength={120} placeholder="Masa y hojas" />
              <Field label="Monto" name="amount" inputMode="decimal" required />
              <Button type="submit" variant="secondary" busy={busy}>Agregar gasto</Button>
            </form>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function Deliveries({ orders, url, busy, run }: { orders: Order[]; url: string; busy: boolean; run: (a: () => Promise<unknown>, ok: string) => Promise<boolean> }) {
  const home = orders.filter((o) => o.delivery);
  const pickup = orders.filter((o) => !o.delivery);
  const zones = [...new Set(home.map((o) => o.zone ?? 'Sin zona'))].sort();
  return (
    <>
      {zones.map((z) => (
        <Card key={z} title={`A domicilio · ${z}`}>
          <ul className="list">
            {home.filter((o) => (o.zone ?? 'Sin zona') === z).map((o) => (
              <li key={o.id} className="list__item list__item--stack">
                <span>
                  <strong>#{o.number} {o.customerName}</strong>{o.phone ? <> · <a href={`tel:${o.phone}`}>{o.phone}</a></> : null} · {STATUS[o.status]} · {o.paid ? 'pagado' : `cobrar ${money(o.totalCents)}`}
                  <span className="small"> · {o.address}{o.addressRefs ? ` (${o.addressRefs})` : ''} · {o.items.map((i) => `${i.quantity} ${i.name}`).join(', ')}{o.driver ? ` · lleva ${o.driver}` : ''}</span>
                </span>
                {o.status !== 'entregado' ? (
                  <form className="family-add" onSubmit={(e) => { e.preventDefault(); const driver = String(new FormData(e.currentTarget).get('driver') ?? '').trim() || null; void run(() => api.put(`${url}/orders/${o.id}/status`, { status: 'entregado', driver }), `Pedido ${o.number} entregado.`); }}>
                    <Field label="Quién lo lleva" name="driver" defaultValue={o.driver ?? ''} maxLength={120} />
                    <Button type="submit" variant="secondary" busy={busy}>Entregado</Button>
                  </form>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      ))}
      <Card title="Se recogen">
        {pickup.length === 0 ? <p className="muted small">Nadie recoge.</p> : null}
        <ul className="list">
          {pickup.map((o) => (
            <li key={o.id} className="list__item">
              <span><strong>#{o.number} {o.customerName}</strong> · {o.items.map((i) => `${i.quantity} ${i.name}`).join(', ')} · {o.paid ? 'pagado' : `cobrar ${money(o.totalCents)}`} · {STATUS[o.status]}</span>
              {o.status !== 'entregado' ? <Button variant="secondary" busy={busy} onClick={() => void run(() => api.put(`${url}/orders/${o.id}/status`, { status: 'entregado' }), `Pedido ${o.number} entregado.`)}>Entregado</Button> : null}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

/** Enlace público para pedir sin cuenta: /r/:slug/:token. */
export function PublicFundraiserPage() {
  const { slug = '', token = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const paid = params.get('pago') === 'aprobado';
  const base = `/commerce/public/fundraisers/${slug}/${token}`;
  const info = useAsync((signal) => api.get<{ churchName: string; title: string; description: string | null; open: boolean; ordersCloseAt: string | null; deliveryDate: string | null; pickupPlace: string | null; deliveryEnabled: boolean; deliveryFeeCents: number; canPayOnline: boolean; products: { id: string; name: string; priceCents: number }[] }>(base, signal), [base]);
  const [done, setDone] = useState<{ number: number; totalCents: number; orderId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="landing">
      <header className="landing__bar">
        <Link to="/" aria-label="KEORA"><Logo /></Link>
      </header>
      <main className="public public-event">
        {info.status === 'loading' ? <Loading /> : null}
        {info.status === 'error' ? <ErrorState error={info.error} /> : null}
        {info.status === 'ready' ? (
          <>
            <p className="eyebrow">{info.data.churchName}</p>
            <h1>{info.data.title}</h1>
            {info.data.description ? <p className="pre-line">{info.data.description}</p> : null}
            <p className="muted">
              {info.data.deliveryDate ? `Entrega: ${day(info.data.deliveryDate)}` : ''}{info.data.pickupPlace ? ` · ${info.data.pickupPlace}` : ''}
              {info.data.ordersCloseAt ? ` · Pedidos hasta el ${day(info.data.ordersCloseAt)}` : ''}
            </p>
            {error ? <Alert>{error}</Alert> : null}
            {paid && !done ? (
              <Card>
                <h2>Pedido pagado</h2>
                <p>¡Gracias! Tu pago fue aprobado.</p>
                {info.data.open ? <Button variant="secondary" onClick={() => setParams({}, { replace: true })}>Hacer otro pedido</Button> : null}
              </Card>
            ) : done ? (
              <Card>
                <h2>¡Pedido #{done.number} recibido!</h2>
                <p>Total: <strong>{money(done.totalCents)}</strong>. Te contactaremos para confirmar.</p>
                {info.data.canPayOnline ? (
                  <Button busy={busy} onClick={() => {
                    setBusy(true);
                    api.post<{ url: string }>(`${base}/orders/${done.orderId}/checkout`).then((r) => window.location.assign(r.url)).catch((e) => { setError(errorMessage(e)); setBusy(false); });
                  }}>Pagar en línea</Button>
                ) : <p className="small muted">Pagas al recibir o como te indiquen.</p>}
              </Card>
            ) : info.data.open ? (
              <Card>
                <OrderForm products={info.data.products} deliveryEnabled={info.data.deliveryEnabled} deliveryFeeCents={info.data.deliveryFeeCents} busy={busy} submitLabel="Hacer mi pedido"
                  onSubmit={async (b) => {
                    setBusy(true);
                    setError(null);
                    try {
                      setDone(await api.post<{ number: number; totalCents: number; orderId: string }>(`${base}/orders`, b));
                      return true;
                    } catch (e) {
                      setError(errorMessage(e));
                      return false;
                    } finally {
                      setBusy(false);
                    }
                  }} />
              </Card>
            ) : <Alert tone="info">La preventa ya cerró. ¡Gracias!</Alert>}
          </>
        ) : null}
      </main>
    </div>
  );
}
